import React from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Loader2, MapPinOff, RefreshCw, ShieldOff } from "lucide-react";
import DsHero from "../../../components/DsHero";
import { TEXTO_SIN_ACCESO } from "./stock/StockComun";
import useTecnicaPermisos from "./useTecnicaPermisos";
import { dependeDeMe, esSeccionStock, etiquetaSeccion, puedeVerSeccion, rutaInicialTecnica, textoSinPermiso } from "./tecnicaPermisos";

/**
 * Guardia, entrada y redirecciones del modo Técnica (las usa TecnicaRoutes.jsx).
 *
 * Quien entra por la URL a algo que no le corresponde ve por qué y un link a lo que sí puede ver:
 * nunca una pantalla en blanco ni un 403 del backend. Las pantallas de stock conservan su propio
 * chequeo (SinAccesoStock) como defensa si alguien las monta sin esta guardia.
 */

/** Adónde mandar a alguien que no puede ver `seccion`: su primera sección, o el panel si no tiene ninguna. */
function LinkDeSalida({ permisos, seccion, aviso }) {
    // Con la sesión caída los permisos no valen: mandarlo a otra sección sería mandarlo a otro error.
    const inicial = aviso === "ERROR_SESION" ? null : rutaInicialTecnica(permisos);
    if (inicial && inicial.id !== seccion) {
        return <Link className="ds-btn ds-btn--secondary" to={inicial.ruta}>Ir a {inicial.label}</Link>;
    }
    return <Link className="ds-btn ds-btn--secondary" to="/home">Volver al panel</Link>;
}

/**
 * Mientras vuelven la sesión y /me. Va dentro del contenido y no es el LoadingOverlay: ése es un
 * portal sobre toda la pantalla que tapaba el menú y el TopNavbar, y si la carga no terminaba no
 * quedaba ni «Menú principal» ni «Cerrar Sesión».
 */
function CargandoTecnica() {
    return (
        <div className="ds-page">
            <div className="ds-empty-state" role="status" aria-live="polite">
                <div className="ds-empty-state__icon"><Loader2 size={26} aria-hidden="true" /></div>
                <h3>Cargando tus permisos de Técnica…</h3>
            </div>
        </div>
    );
}

/**
 * El porqué, en este orden: falló la sesión entera (no hay rol ni módulos, así que cualquier otro
 * texto mentiría); sin módulo; /me falló (los flags que salen de /me quedan en false y sin
 * esto se leería como «no tenés permiso»), sólo si la sección depende de /me: lo que niega el rol
 * no lo destraba recargar; legajo duplicado en lo de stock; y el texto de la sección.
 */
function porQue(seccion, permisos, aviso) {
    if (aviso === "ERROR_SESION") return { texto: "No se pudo cargar tu sesión. Recargá la página.", recargar: true };
    if (!permisos.tieneModulo) return { texto: "Tu empresa no tiene el módulo Técnica." };
    if (aviso === "ERROR_ME" && dependeDeMe(permisos, seccion)) return { texto: TEXTO_SIN_ACCESO.ERROR_ME, recargar: true };
    if (aviso === "LEGAJO_DUPLICADO" && esSeccionStock(seccion)) return { texto: TEXTO_SIN_ACCESO.LEGAJO_DUPLICADO };
    return textoSinPermiso(permisos, seccion);
}

export function TecnicaSinPermiso({ seccion, permisos, aviso = null }) {
    const { texto, detalle, recargar } = porQue(seccion, permisos, aviso);
    return (
        <div className="ds-page">
            <DsHero icon={ShieldOff} eyebrow="Técnica" title={etiquetaSeccion(permisos, seccion)} />
            <div className="ds-empty-state" role="alert">
                <div className="ds-empty-state__icon"><ShieldOff size={26} /></div>
                <h3>{texto}</h3>
                {detalle && <p>{detalle}</p>}
                <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 12 }}>
                    {recargar && (
                        <button type="button" className="ds-btn ds-btn--primary" onClick={() => window.location.reload()}>
                            <RefreshCw size={15} />
                            <span>Recargar</span>
                        </button>
                    )}
                    <LinkDeSalida permisos={permisos} seccion={seccion} aviso={aviso} />
                </div>
            </div>
        </div>
    );
}

/**
 * Monta `children` sólo si se puede ver la sección. `seccion` es un id de SECCIONES_TECNICA o una
 * función de los params de la ruta (las solapas de stock dependen de :solapa).
 */
export function TecnicaRuta({ seccion, children }) {
    const params = useParams();
    const { listo, permisos, aviso } = useTecnicaPermisos();
    const id = typeof seccion === "function" ? seccion(params) : seccion;
    if (!listo) return <CargandoTecnica />;
    // Con la sesión caída el rol y los módulos pueden ser los de antes (p. ej. un cambio de empresa
    // que falló): no se monta la pantalla, que pediría todo con una sesión rota.
    if (aviso !== "ERROR_SESION" && puedeVerSeccion(permisos, id)) return children;
    return <TecnicaSinPermiso seccion={id} permisos={permisos} aviso={aviso} />;
}

/** /admin/tecnica: la primera sección que la persona puede ver (antes era el hub con solapas). */
export function TecnicaInicio() {
    const { listo, permisos, aviso } = useTecnicaPermisos();
    if (!listo) return <CargandoTecnica />;
    const inicial = aviso === "ERROR_SESION" ? null : rutaInicialTecnica(permisos);
    if (inicial) return <Navigate to={inicial.ruta} replace />;
    return <TecnicaSinPermiso seccion="ordenes" permisos={permisos} aviso={aviso} />;
}

/**
 * Redirige una ruta vieja a la nueva conservando la query y el hash (puede haber links viejos en
 * mails, WhatsApp o favoritos). Con `replace`, para que Atrás no vuelva a la vieja y rebote.
 *
 * @param {{ a: (params: object) => string }} props
 */
export function RedireccionTecnica({ a }) {
    const params = useParams();
    const { search, hash } = useLocation();
    return <Navigate to={`${a(params)}${search}${hash}`} replace />;
}

/** Cualquier otra ruta bajo /admin/tecnica: el <Routes> del Layout no tiene catch-all y quedaría en blanco. */
export function TecnicaRutaDesconocida() {
    const { listo, permisos } = useTecnicaPermisos();
    if (!listo) return <CargandoTecnica />;
    const inicial = rutaInicialTecnica(permisos);
    return (
        <div className="ds-page">
            <DsHero icon={MapPinOff} eyebrow="Técnica" title="Página inexistente" />
            <div className="ds-empty-state" role="alert">
                <div className="ds-empty-state__icon"><MapPinOff size={26} /></div>
                <h3>Esa página de Técnica no existe.</h3>
                <p>Puede ser un link viejo o mal copiado.</p>
                <div style={{ marginTop: 12 }}>
                    {inicial
                        ? <Link className="ds-btn ds-btn--secondary" to={inicial.ruta}>Ir a {inicial.label}</Link>
                        : <Link className="ds-btn ds-btn--secondary" to="/home">Volver al panel</Link>}
                </div>
            </div>
        </div>
    );
}
